import React from "react";
import moment from "moment";
import "moment-timezone";
import { connect, ConnectedProps } from "react-redux";

import errorWhiteSvg from "assets/img/error-white.svg";
import { Body } from "components/Typography";
import { State } from "state";
import { unsetError, FileUploadState } from "state/create_file_upload";
import { fetchUsersByProjectId } from "state/users";
import { PaginatedQuery } from "interfaces/paginated-query";
import { LightThemeTable } from "components/LightThemeTable";

interface UserPreviewOwnProps {
  prefix: string;
  errorPrefixes?: string[];
  projectId: string;
  showError?: boolean;
}

type UserPreviewProps = UserPreviewOwnProps & PropsFromRedux;

class NewUserPreview extends React.Component<UserPreviewProps> {
  private USER_PREVIEW_PAGE_SIZE = 10;
  private USER_PREVIEW_DEFAULT_SORT = "";

  private defaultPaginationParams: PaginatedQuery = {
    sort: this.USER_PREVIEW_DEFAULT_SORT,
    size: this.USER_PREVIEW_PAGE_SIZE,
    page: 0,
  };

  public state = { 
    ...this.defaultPaginationParams, 
    initialMountComplete: false,
    currentPage: 1,
    limit: 10,
  };

  public componentDidMount() {
    const { projectId } = this.props;
    this.props.fetchUsersByProjectId(projectId);
    this.setState({ initialMountComplete: true });
  }

  public componentWillUnmount() {
    const { prefix, errorPrefixes } = this.props;
    this.props.unsetError(prefix);
    if (errorPrefixes) {
      errorPrefixes.forEach((errorPrefix) => this.props.unsetError(errorPrefix));
    }
  }

  public componentDidUpdate(prevProps: UserPreviewProps) {
    const { projectId } = this.props;

    // Re-fetch when upload finishes
    if (
      (prevProps.upload.loading && !this.props.upload.loading && !this.props.upload.hasError) ||
      (prevProps.errorUpload &&
        prevProps.errorUpload.loading &&
        this.props.errorUpload &&
        !this.props.errorUpload.loading &&
        !this.props.errorUpload.hasError)
    ) {
      this.props.fetchUsersByProjectId(projectId);
    }
  }

  private handlePageChange = (page: number, newLimit: number) => {
    this.setState({ currentPage: page, limit: newLimit });
  };

  public render() {
    const {
      upload: { hasError: defaultError, errors: defaultErrors = [] },
      errorUpload,
      users,
      showError = true,
    } = this.props;

    if (!users) return null;

    const hasError = defaultError || (errorUpload && errorUpload.hasError);
    const errors: string[] = defaultErrors.concat((errorUpload && errorUpload.errors) || []);

    const cellStyle: React.CSSProperties = { paddingTop: "8px" , paddingBottom:"8px"};

    const tableColumnsConfig = [
      {
        field: "displayName",
        header: "Name",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>{row?.displayName || "-"}</div>
        ),
      },
      {
        field: "company",
        header: "Company",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>{row?.company || "-"}</div>
        ),
      },
      {
        field: "role",
        header: "Role",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>{row?.role || "-"}</div>
        ),
      },
      {
        field: "email",
        header: "Email",
        formatter: (_: any, row: any) => (
          <div style={cellStyle}>{row?.email || "-"}</div>
        ),
      },
    ];

    return (
      <>
        {hasError && (
          <div className="error card-error">
            <img src={errorWhiteSvg} alt="Error Icon" />
            <p className="text">Error: File formatting. See above for details.</p>
          </div>
        )}

        {this.state.initialMountComplete && (
          <LightThemeTable
            data={users}
            columns={tableColumnsConfig}
            onPageChange={this.handlePageChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            isLoading={this.props.loadingUsers}
          />
        )}

        {showError && hasError && (
          <div className="card-error-bottom">
            <h5 className="error">Errors:</h5>
            <ul className="error-list">
              {errors.map((e) => (
                <li key={e}>
                  <Body type="3">{e}</Body>
                </li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  }
}

const connecter = connect(
  (state: State, ownProps: UserPreviewOwnProps) => {
    const { prefix, errorPrefixes = [] } = ownProps;

    let errorUpload: FileUploadState = { hasError: false };
    errorPrefixes.some((errorPrefix) => {
      // @ts-ignore
      if (state[`${errorPrefix}_file_upload`].hasError) {
        // @ts-ignore
        errorUpload = state[`${errorPrefix}_file_upload`];
        return true;
      }
      return false;
    });

    return {
      errorUpload,
      upload: {
        // @ts-ignore
        ...state[`${prefix}_file_upload`],
        hasError: errorUpload.hasError,
      },
      users: state.users.content?.data,
      loadingUsers: state.users.loading,
    };
  },
  { fetchUsersByProjectId, unsetError }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;

export default connecter(NewUserPreview);
